import React, { useEffect, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Dispatch } from "redux";
import { format } from "date-fns";
import { Button, Card, FormGroup, Intent, Spinner, Alert, Icon } from "@blueprintjs/core";
import { IStore } from "redux/store";
import { rootRoutes } from "routes";
import Select from "react-select";
import toolSelectors from "redux/app/tool/selectors";
import { getTenantsOptionList, getVesselsOptionList } from "redux/app/tool/utils";
import toolActions from "redux/app/tool/actions";
import { IOption } from "redux/app/feedback/@types";
import { clearIndicatorStyles, controlStyles, dropdownIndicatorStyles, menuStyles, optionStyles, placeholderStyles } from "redux/app/feedback/utils";
import clsx from "clsx";

type OwnProps = {};
type IdlEventsProps = PropsFromRedux & OwnProps;

interface IdlEvent {
  _id: string;
  _rev: string;
  date: string;
  status: string;
  createdAt: string;
  authorizedBy: {
    id: string;
    name: string;
    designation: string;
    staffId: string;
    timestamp: string;
  };
  remarks: string;
  vesselId: string;
  tenantId: string;
  vesselName: string;
  modelName: string;
}

function IdlEvents(props: IdlEventsProps) {
  const [selectedTenant, setSelectedTenant] = useState<IOption | undefined>(undefined);
  const [selectedVessel, setSelectedVessel] = useState<IOption | undefined>(undefined);
  const [startDate, setStartDate] = useState<string>(
    format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), "yyyy-MM-dd")
  );
  const [endDate, setEndDate] = useState<string>(
    format(new Date(), "yyyy-MM-dd")
  );
  const [newEventDate, setNewEventDate] = useState<string>(format(new Date(), "yyyy-MM-dd"));
  const [newEventStatus, setNewEventStatus] = useState<"SKIPPED" | "REPEATED">("SKIPPED");
  const [newEventRemarks, setNewEventRemarks] = useState<string>("");
  const [addEventError, setAddEventError] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<IdlEvent | null>(null);
  const isCreatingEvent = props.jobToCreateIdlEvent.isLoading;

  useEffect(() => {
    props.loadTenantsList();
  }, []);

  useEffect(() => {
    if (selectedTenant?.value) {
      props.loadTenantSpecificVesselsList(selectedTenant.value);
      setSelectedVessel(undefined); // Reset vessel selection when tenant changes
    }
  }, [selectedTenant]);

  const fetchEvents = async () => {
    if (!selectedTenant?.value || !selectedVessel?.value) return;
    
    setLoading(true);
    try {
      props.loadIdlEvents(selectedTenant.value, selectedVessel.value, startDate, endDate);
    } catch (error) {
      console.error("Error fetching IDL events:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (event: IdlEvent) => {
    setEventToDelete(event);
  };

  const handleDeleteConfirm = async () => {
    if (!eventToDelete) return;
    
    try {
      props.deleteIdlEvent(eventToDelete._id);
      // Refresh events list after deletion
      fetchEvents();
    } catch (error) {
      console.error("Error deleting IDL event:", error);
    } finally {
      setEventToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setEventToDelete(null);
  };

  const handleCreateEvent = () => {
    if (!selectedTenant?.value || !selectedVessel?.value || !newEventDate) {
      setAddEventError("Tenant, vessel and date are required to add an event.");
      return;
    }
    if (!newEventRemarks.trim()) {
      setAddEventError("Remarks are required to add an event.");
      return;
    }

    const isDuplicateDate = props.idlEvents.some(
      (event) => event.date?.split("T")[0] === newEventDate
    );
    if (isDuplicateDate) {
      setAddEventError(`An IDL event already exists for ${newEventDate}.`);
      return;
    }

    setAddEventError("");
    props.createIdlEvent(
      newEventDate,
      newEventStatus,
      newEventRemarks.trim(),
      selectedVessel.value,
      selectedTenant.value,
      selectedVessel.label
    );
    setNewEventRemarks("");
    fetchEvents();
  };

  return (
    <div className="min-h-[calc(100vh-98px)] bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 md:p-8 lg:p-12">
      <div className="max-w-[1600px] mx-auto space-y-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">IDL Event Manager</h1>
          <p className="text-gray-600">Manage International Date Line events for vessels</p>
        </div>

        <Card className="shadow-sm">
          <div className="p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Search Filters</h2>
            <div className="space-y-4">
              {/* Vessel Selection Group */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FormGroup label="Select Tenant" labelInfo="(required)" className="mb-0">
                  <Select
                    id="tenants"
                    isClearable
                    isSearchable
                    key={"tenants" + selectedTenant?.value}
                    placeholder={"Search tenants"}
                    isDisabled={loading || props.jobToLoadVesselsSpecificToTenant.isLoading}
                    value={selectedTenant}
                    options={getTenantsOptionList(props.tenants)}
                    isLoading={props.jobToLoadTenants.isLoading}
                    onChange={(newValue) => {
                      setSelectedTenant(newValue ?? undefined);
                    }}
                    className="w-full text-sm"
                    menuPosition="fixed"
                    classNames={{
                      option: ({ isFocused, isSelected }) =>
                        clsx(
                          isFocused && optionStyles.focus,
                          isSelected && optionStyles.selected,
                          optionStyles.base
                        ),
                      dropdownIndicator: () => dropdownIndicatorStyles,
                      menu: () => menuStyles,
                      clearIndicator: () => clearIndicatorStyles,
                      placeholder: () => placeholderStyles,
                      control: ({ isFocused, isDisabled }) =>
                        clsx(
                          isFocused ? controlStyles.focus : controlStyles.nonFocus,
                          isDisabled ? controlStyles.disabled : controlStyles.base
                        ),
                      valueContainer: ({ isDisabled }) =>
                        clsx("pl-1", isDisabled && "bg-gray-200 ml-2"),
                    }}
                  />
                </FormGroup>

                <FormGroup label="Select Vessel" labelInfo="(required)" className="mb-0">
                  <Select
                    id="vessels"
                    isClearable
                    isSearchable
                    key={"vessels" + selectedVessel?.value}
                    placeholder={"Search vessels"}
                    isDisabled={!selectedTenant?.value || loading || props.jobToLoadVesselsSpecificToTenant.isLoading}
                    value={selectedVessel}
                    options={getVesselsOptionList(props.vessels)}
                    isLoading={props.jobToLoadVesselsSpecificToTenant.isLoading}
                    onChange={(newValue) => {
                      setSelectedVessel(newValue ?? undefined);
                    }}
                    className="w-full text-sm"
                    menuPosition="fixed"
                    classNames={{
                      option: ({ isFocused, isSelected }) =>
                        clsx(
                          isFocused && optionStyles.focus,
                          isSelected && optionStyles.selected,
                          optionStyles.base
                        ),
                      dropdownIndicator: () => dropdownIndicatorStyles,
                      menu: () => menuStyles,
                      clearIndicator: () => clearIndicatorStyles,
                      placeholder: () => placeholderStyles,
                      control: ({ isFocused, isDisabled }) =>
                        clsx(
                          isFocused ? controlStyles.focus : controlStyles.nonFocus,
                          isDisabled ? controlStyles.disabled : controlStyles.base
                        ),
                      valueContainer: ({ isDisabled }) =>
                        clsx("pl-1", isDisabled && "bg-gray-200 ml-2"),
                    }}
                  />
                </FormGroup>
              </div>

              {/* Date Range Group */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FormGroup label="Start Date" className="mb-0">
                  <input
                    type="date"
                    className="bp3-input w-full"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    max={endDate}
                  />
                </FormGroup>

                <FormGroup label="End Date" className="mb-0">
                  <input
                    type="date"
                    className="bp3-input w-full"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate}
                  />
                </FormGroup>
              </div>

              {/* Action Button */}
              <div className="flex justify-end pt-1">
                <Button
                  intent={Intent.PRIMARY}
                  onClick={fetchEvents}
                  disabled={
                    !selectedTenant?.value ||
                    !selectedVessel?.value ||
                    loading
                  }
                  loading={loading}
                  className="min-w-[120px]"
                >
                  Fetch Events
                </Button>
              </div>
            </div>
          </div>
        </Card>

        <Card className="shadow-sm">
          <div className="p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Add IDL Event</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <FormGroup label="Event Date" labelInfo="(required)" className="mb-0">
                  <input
                    type="date"
                    className="bp3-input w-full"
                    value={newEventDate}
                    onChange={(e) => setNewEventDate(e.target.value)}
                  />
                </FormGroup>
                <FormGroup label="Status" labelInfo="(required)" className="mb-0">
                  <select
                    className="bp3-input w-full"
                    value={newEventStatus}
                    onChange={(e) => setNewEventStatus(e.target.value as "SKIPPED" | "REPEATED")}
                  >
                    <option value="SKIPPED">SKIPPED</option>
                    <option value="REPEATED">REPEATED</option>
                  </select>
                </FormGroup>
                <FormGroup label="Remarks" labelInfo="(required)" className="mb-0">
                  <input
                    type="text"
                    className="bp3-input w-full"
                    value={newEventRemarks}
                    onChange={(e) => setNewEventRemarks(e.target.value)}
                    placeholder="Enter remarks"
                  />
                </FormGroup>
              </div>
              {addEventError ? (
                <div className="text-sm text-red-600">{addEventError}</div>
              ) : null}
              <div className="flex justify-end">
                <Button
                  intent={Intent.SUCCESS}
                  onClick={handleCreateEvent}
                  loading={isCreatingEvent}
                  disabled={
                    !selectedTenant?.value ||
                    !selectedVessel?.value ||
                    !newEventDate ||
                    !newEventRemarks.trim() ||
                    isCreatingEvent ||
                    loading
                  }
                >
                  Add Event
                </Button>
              </div>
            </div>
          </div>
        </Card>

        <Card className="shadow-sm">
          <div className="p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Events List</h2>
            <div className="overflow-x-auto rounded-lg border border-gray-200 relative">
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-10">
                  <Spinner size={40} intent={Intent.PRIMARY} />
                </div>
              )}
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">S.No</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Authorized By</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remarks</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="px-4 sm:px-6 py-8 text-center">
                        <div className="flex justify-center items-center space-x-2">
                          <Spinner size={20} />
                          <span className="text-gray-500">Loading events...</span>
                        </div>
                      </td>
                    </tr>
                  ) : props.idlEvents.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 sm:px-6 py-8 text-center text-sm text-gray-500">
                        No events found for the selected criteria
                      </td>
                    </tr>
                  ) : (
                    props.idlEvents.map((event, index) => (
                      <tr key={event._id} className="hover:bg-gray-50">
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {index + 1}
                        </td>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span title={event.date}>{event.date.split('T')[0]}</span>
                        </td>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm">
                          <span className={clsx(
                            "px-2 py-1 text-xs font-medium rounded-full",
                            event.status === "SKIPPED" ? "bg-yellow-100 text-yellow-800" : "bg-green-100 text-green-800"
                          )}>
                            {event.status}
                          </span>
                        </td>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div>
                            <div className="font-medium">{event.authorizedBy.name}</div>
                            <div className="text-gray-500">{event.authorizedBy.designation}</div>
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4 text-sm text-gray-900">
                          <div className="max-w-xs truncate">{event.remarks}</div>
                        </td>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span title={event.createdAt}>
                            {format(new Date(event.createdAt), "MMM dd, yyyy hh:mm a")}
                          </span>
                        </td>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <Button
                            intent={Intent.DANGER}
                            minimal
                            onClick={() => handleDeleteClick(event)}
                            className="hover:bg-red-50"
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Card>
      </div>

      <Alert
        isOpen={!!eventToDelete}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        intent={Intent.DANGER}
        icon={null}
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
      >
        <div className="flex items-center gap-2 mb-3">
          <Icon icon="trash" size={18} className="text-red-600" />
          <span className="font-semibold text-lg text-gray-900">Delete IDL Event?</span>
        </div>
        <div className="mb-3 text-sm text-gray-700">
          This action cannot be undone.
        </div>
        {eventToDelete && (
          <div className="bg-gray-50 rounded px-3 py-2 mb-3 text-sm text-gray-800">
            <div><span className="font-medium">Date:</span> {eventToDelete.date.split('T')[0]}</div>
            <div><span className="font-medium">Status:</span> {eventToDelete.status}</div>
            <div><span className="font-medium">Authorized By:</span> {eventToDelete.authorizedBy.name}</div>
          </div>
        )}
      </Alert>
    </div>
  );
}

function mapStateToProps(store: IStore, ownProps: OwnProps) {
  const toolStore = toolSelectors._getStore(store);
  return {
    tenants: toolSelectors.getTenants(toolStore),
    vessels: toolSelectors.getVessels(toolStore),
    jobToLoadTenants: toolSelectors.jobToLoadTenants(toolStore),
    jobToLoadVesselsSpecificToTenant: toolSelectors.jobToLoadVesselsSpecificToTenant(toolStore),
    jobToCreateIdlEvent: toolSelectors.jobToCreateIdlEvent(toolStore),
    idlEvents: toolSelectors.getIdlEvents(toolStore),
  };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
  return {
    loadTenantsList() {
      dispatch(toolActions.commands.toolTenantListLoad());
    },
    loadTenantSpecificVesselsList(tenantId: string) {
      dispatch(toolActions.commands.toolTenantSpecificVesselLoad(tenantId));
    },
    loadIdlEvents(tenantId: string, vesselId: string, startDate: string, endDate: string) {
      dispatch(toolActions.commands.toolIdlEventsLoad(tenantId, vesselId, startDate, endDate));
    },
    createIdlEvent(
      date: string,
      status: "SKIPPED" | "REPEATED",
      remarks: string,
      vesselId: string,
      tenantId: string,
      vesselName?: string
    ) {
      dispatch(
        toolActions.commands.toolIdlEventCreate(
          date,
          status,
          remarks,
          vesselId,
          tenantId,
          vesselName
        )
      );
    },
    deleteIdlEvent(eventId: string) {
      dispatch(toolActions.commands.toolIdlEventDelete(eventId));
    },
  };
}

const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(IdlEvents); 